import type { ApiDtoWithPb } from "store/slices/apisV2/slice";

export type DiscardedEdit = {
  propertyName: string;
  reason: string;
  propertyValue: unknown;
};

type BaseComponentAction = {
  id: string;
  componentName: string;
};

export type SetAction = BaseComponentAction & {
  action: "set";
  property: string;
  value: unknown;
};

type ShowAlertTrigger = {
  type: "showAlert";
  message: string;
  style: "info" | "warning" | "error" | "success" | "default";
  alertDuration?: number;
  alertPosition?:
    | "bottomRight"
    | "bottomLeft"
    | "topRight"
    | "topLeft"
    | "top"
    | "bottom";
  [key: string]: unknown;
};

type RunJsTrigger = {
  type: "runJs";
  code: string;
  [key: string]: unknown;
};

type ExecuteApiTrigger = {
  type: "executeApi";
  apiNames: string[];
  [key: string]: unknown;
};

type CancelApiTrigger = {
  type: "cancelApi";
  apiNames: string[];
  [key: string]: unknown;
};

type ControlModalTrigger = {
  type: "controlModal";
  name: string;
  direction: "open" | "close";
  [key: string]: unknown;
};

type ControlSlideoutTrigger = {
  type: "controlSlideout";
  name: string;
  direction: "open" | "close";
  [key: string]: unknown;
};

type NavigateToTrigger = {
  type: "navigateTo";
  url: string;
  newWindow: boolean;
  replaceHistory: boolean;
  [key: string]: unknown;
};

type ResetStateVarTrigger = {
  type: "resetStateVar";
  state: {
    name: string;
    scope: string;
  };
  [key: string]: unknown;
};

type SetStateVarTrigger = {
  type: "setStateVar";
  state: {
    name: string;
    scope: string;
  };
  value: string;
  [key: string]: unknown;
};

type ControlTimerTrigger = {
  type: "controlTimer";
  command: "start" | "stop" | "toggle";
  scope?: string;
  name: string;
  [key: string]: unknown;
};

type TriggerEventTrigger = {
  type: "triggerEvent";
  event: {
    name: string;
    scope: string;
  };
  eventPayload?: Record<string, any>;
  [key: string]: unknown;
};

type SetProfileTrigger = {
  type: "setProfile";
  profileAction: "set" | "unset";
  profileId: string;
  [key: string]: unknown;
};

type SetComponentPropertyTrigger = {
  type: "setComponentProperty";
  component: string;
  propertyName: string;
  propertyValue: string;
  [key: string]: unknown;
};

type ResetComponentPropertyTrigger = {
  type: "resetComponent";
  component: string;
  propertyName: string;
  [key: string]: unknown;
};

type NavigateToRouteTrigger = {
  type: "navigateToRoute";
  routePathDescriptor: string;
  [key: string]: unknown;
};

export type AddEventActionValue =
  | SetStateVarTrigger
  | ControlTimerTrigger
  | TriggerEventTrigger
  | SetProfileTrigger
  | SetComponentPropertyTrigger
  | ResetComponentPropertyTrigger
  | NavigateToRouteTrigger
  | ResetStateVarTrigger
  | RunJsTrigger
  | ExecuteApiTrigger
  | CancelApiTrigger
  | ControlModalTrigger
  | ControlSlideoutTrigger
  | NavigateToTrigger
  | ShowAlertTrigger;

export type AddEventAction = BaseComponentAction & {
  action: "add";
  property: string;
  value: AddEventActionValue;
};

export type RemoveEventAction = BaseComponentAction & {
  action: "remove";
  property: string;
  value: {
    id: string;
    type?: "remove";
  };
};
export type ResetPropertyAction = BaseComponentAction & {
  action: "reset";
  property: string;
};

export type RemovePropertyAction = BaseComponentAction & {
  action: "remove";
  property: string;
};

export type ComponentCreateAction = BaseComponentAction & {
  action: "createComponent";
  componentType: string;
  componentName: string;
};

export type ComponentRemoveAction = BaseComponentAction & {
  action: "removeComponent";
  componentType: string;
  componentName: string;
};

export type MoveComponentAction = BaseComponentAction & {
  action: "moveComponent";
  where: "above" | "below" | "left" | "right" | "inside";
  relativeTo: string;
};

/* table column actions */
export type SetColumnAction = SetAction & {
  table_column: string;
};

type RemoveColumnPropertyAction = RemovePropertyAction & {
  table_column: string;
};

export type RemoveColumnEventAction = RemoveEventAction & {
  table_column: string;
};

export type AddColumnEventAction = AddEventAction & {
  table_column: string;
};

export type RemoveColumnAction = BaseComponentAction & {
  action: "remove";
  table_column: string;
  property: undefined;
  value: undefined;
};

export type ColumnAction =
  | SetColumnAction
  | RemoveColumnPropertyAction
  | RemoveColumnEventAction
  | AddColumnEventAction
  | RemoveColumnAction;

/* tab actions */

export type SetTabAction = SetAction & {
  tabs_tab: string;
};

export type RemoveTabAction = BaseComponentAction & {
  action: "remove";
  tabs_tab: string;
  property?: undefined;
  value?: undefined;
};

export type RemoveTabPropertyAction = RemovePropertyAction & {
  tabs_tab: string;
};

export type TabAction =
  | RemoveTabAction
  | RemoveTabPropertyAction
  | SetTabAction;

/* section column actions */
type SetSectionColumnAction = SetAction & {
  section_column: string;
};

type RemoveSectionColumnAction = BaseComponentAction & {
  action: "remove";
  property?: undefined;
  value?: undefined;
};

export type SectionColumnAction =
  | SetSectionColumnAction
  | RemoveSectionColumnAction;

/* menu item actions */
type SetMenuItemAction = SetAction & {
  menu_item: string;
};

type ResetMenuItemAction = ResetPropertyAction & {
  menu_item: string;
};

type RemoveMenuItemEventHandlerAction = RemoveEventAction & {
  menu_item: string;
};

type AddMenuItemPropertyAction = AddEventAction & {
  menu_item: string;
};

type RemoveMenuItemAction = BaseComponentAction & {
  action: "remove";
  menu_item: string;
  property: undefined;
  value: undefined;
};

export type MenuItemAction =
  | SetMenuItemAction
  | ResetMenuItemAction
  | RemoveMenuItemEventHandlerAction
  | AddMenuItemPropertyAction
  | RemoveMenuItemAction;

/* kv item actions */

export type SetKvPropertyAction = SetAction & {
  key_value_property: string;
};

type AddKvEventAction = AddEventAction & {
  key_value_property: string;
};

type RemoveKvEventAction = RemoveEventAction & {
  key_value_property: string;
};

type ResetKvPropertyAction = ResetPropertyAction & {
  key_value_property: string;
};

export type RemoveKvItemAction = RemovePropertyAction & {
  key_value_property: string;
};

export type KvAction =
  | RemoveKvItemAction
  | SetKvPropertyAction
  | AddKvEventAction
  | RemoveKvEventAction
  | ResetKvPropertyAction;

/* all actions */
export type ComponentEditAction =
  | SetAction
  | AddEventAction
  | RemoveEventAction
  | ResetPropertyAction
  | RemovePropertyAction
  | ColumnAction
  | TabAction
  | MenuItemAction
  | KvAction
  | SectionColumnAction;

export type ClarkComponentAction =
  | ComponentEditAction
  | ComponentCreateAction
  | ComponentRemoveAction;

export type ClarkAction =
  | ClarkComponentAction
  | ClarkApiAction
  | ClarkStateVarAction
  | ClarkTimerAction
  | ClarkEventAction
  | MoveComponentAction;

type ClarkApiAction =
  | ClarkCreateApiAction
  | ClarkEditApiAction
  | ClarkScaffoldApiAction
  | ClarkCompleteApiAction;

export type ClarkCreateApiAction = {
  id: string;
  action: "createApi";
  apiName: string;
};

export type ClarkScaffoldApiAction = {
  id: string;
  action: "scaffoldApi";
  apiName: string;
  apiSpec: {
    name: string;
    shape: string;
    block: Record<string, unknown>;
  }[]; // APIv2 Spec
};

export type ClarkEditApiAction = {
  id: string;
  action: "editApi";
  apiName: string;
  stepName: string;
  stepEdit: {
    action: "set";
    property: string;
    value: unknown;
  };
};

export type ClarkCompleteApiAction = {
  id: string;
  action: "completeApi";
  apiName: string;
};

export type ClarkCreateStateVarAction = {
  id: string;
  action: "createStateVar";
  variableName: string;
};

export type ClarkEditStateVarAction = {
  id: string;
  action: "editStateVar";
  variableName: string;
  property: string;
  value: unknown;
};

type ClarkStateVarAction = ClarkCreateStateVarAction | ClarkEditStateVarAction;

export type ClarkCreateTimerAction = {
  id: string;
  action: "createTimer";
  timerName: string;
};

export type ClarkEditTimerAction = {
  id: string;
  action: "editTimer";
  timerName: string;
  property: string;
  value: unknown;
};

type ClarkTimerAction = ClarkCreateTimerAction | ClarkEditTimerAction;

export type ClarkCreateEventAction = {
  id: string;
  action: "createEvent";
  eventName: string;
};

export type ClarkEditEventAction = {
  id: string;
  action: "editEvent";
  eventName: string;
  property: string;
  value: unknown;
};

type ClarkEventAction = ClarkCreateEventAction | ClarkEditEventAction;

export type EditMetadata = {
  // for tabs and menus, we need to maintain a mapping of ids to indices
  // clark AI operates on index, so if items are added or removed, we need to update the index
  idsToIndices: Record<string, number>;
};

/** Shape produced by the former ClarkProcessor; kept for `setAiChanges` typing. */
export type ClarkProcessorResult = {
  widgetChangedKeys?: Record<string, string[]>;
  widgetChanges: Record<string, Record<string, unknown> | null>;
  widgetMovesById?: Record<
    string,
    {
      position: "left" | "right" | "above" | "below" | "inside";
      relativeTo: string;
    }
  >;
  apiChanges?: Record<string, ApiDtoWithPb>;
  changedApiBlockPaths?: Record<string, string[]>;
  stateVariableChanges?: Record<string, Record<string, unknown> | null>;
  stateVariableChangedKeys?: Record<string, string[]>;
  timerChanges?: Record<string, Record<string, unknown> | null>;
  timerChangedKeys?: Record<string, string[]>;
  eventChanges?: Record<string, Record<string, unknown> | null>;
  eventChangedKeys?: Record<string, string[]>;
  renamesById?: Record<string, string>;
  originalNamesById?: Record<string, string>;
  discardedEdits: Record<string, DiscardedEdit[]>;
  metadataByWidgetId?: Record<string, EditMetadata | undefined>;
};

export enum RESPONSE_SCHEMA_TYPE {
  MULTI_COMPONENT_EVENT = "MULTI_COMPONENT_EVENT",
  MOVE_COMPONENT_EVENT = "MOVE_COMPONENT_EVENT",
  PROPERTIES_EVENT = "PROPERTIES_EVENT",
  SINGLE_EDIT_EVENT = "SINGLE_EDIT_EVENT",
  REQUEST_COMPONENT_UPDATE = "REQUEST_COMPONENT_UPDATE",
  API_SCAFFOLDING_EVENT = "API_SCAFFOLDING_EVENT",
  API_COMPLETED_EVENT = "API_COMPLETED_EVENT",
  SDLC_IMPLEMENT_EVENT = "SDLC_IMPLEMENT_EVENT",
  SDLC_TEST_EVENT = "SDLC_TEST_EVENT",
  SDLC_TEST_DEBUG_EVENT = "SDLC_TEST_DEBUG_EVENT",
}

export type ResultHistoryItem = {
  prompt: string;
  status: "accept" | "discard";
  entitiesState: Record<string, any>;
};

type SldcMessageTestStatus = "pending" | "submitted";

export type SldcMessage = {
  id: string;
  message: string;
  isDemo?: boolean;
} & (
  | { type: RESPONSE_SCHEMA_TYPE.SDLC_IMPLEMENT_EVENT; actionsCount: number }
  | {
      type: RESPONSE_SCHEMA_TYPE.SDLC_TEST_EVENT;
      status: SldcMessageTestStatus;
      answer?: string;
    }
  | {
      type: RESPONSE_SCHEMA_TYPE.SDLC_TEST_DEBUG_EVENT;
    }
);

export type SldcMessages = Array<SldcMessage>;

export type ClarkWebSocketConnectionClosedReason =
  | "manualDisconnect"
  | "gracefulDisconnect"
  | "serverError";

export enum ClarkFocusedEntityType {
  WIDGET = "WIDGET",
  API_BLOCK = "API_BLOCK",
  // todo later
  // API = "API",
  // STATE_VAR = "STATE_VAR",
  // TIMER = "TIMER",
  // EVENT = "EVENT",
}
